'use client'
import { useCurrency } from '@payloadcms/plugin-ecommerce/client/react'
import React, { useMemo } from 'react'

type BaseProps = {
  className?: string
  currencyCodeClassName?: string
  as?: 'span' | 'p'
  size?: 'default' | 'large'
  /** When provided, renders this as the struck-through original price alongside the current (discounted) price */
  originalAmount?: number
  /**
   * Colour of the struck-through original price. Defaults to the muted
   * foreground token (readable on light cards). Pass e.g. 'text-white/50'
   * when the price sits on a dark/glass surface.
   */
  originalClassName?: string
}

type PriceFixed = {
  amount: number
  currencyCode?: string
  highestAmount?: never
  lowestAmount?: never
}

type PriceRange = {
  amount?: never
  currencyCode?: string
  highestAmount: number
  lowestAmount: number
}

type Props = BaseProps & (PriceFixed | PriceRange)

export const Price = ({
  amount,
  className,
  highestAmount,
  lowestAmount,
  currencyCode: currencyCodeFromProps,
  as = 'p',
  size = 'default',
  originalAmount,
  originalClassName = 'text-muted-foreground',
}: Props & React.ComponentProps<'p'>) => {
  const { formatCurrency, supportedCurrencies } = useCurrency()

  const Element = as

  const sizeClasses = size === 'large' ? 'text-4xl font-bold tracking-tight text-[var(--neon-orange)]' : ''
  const combinedClassName = `${className || ''} ${sizeClasses}`.trim()

  const currencyToUse = useMemo(() => {
    if (currencyCodeFromProps) {
      return supportedCurrencies.find((currency) => currency.code === currencyCodeFromProps)
    }
    return undefined
  }, [currencyCodeFromProps, supportedCurrencies])

  if (typeof amount === 'number') {
    // formatCurrency expects cents, so multiply by 100 (11.00 -> 1100)
    // Round to nearest integer to eliminate floating-point precision artifacts
    const amountInCents = Math.round(amount * 100)

    // If originalAmount is provided, show strike-through original + discounted price
    if (typeof originalAmount === 'number' && originalAmount > amount) {
      const originalInCents = Math.round(originalAmount * 100)
      return (
        <Element className={combinedClassName} suppressHydrationWarning>
          <span className={`line-through font-normal text-[0.8em] mr-1.5 ${originalClassName}`}>
            {formatCurrency(originalInCents, { currency: currencyToUse })}
          </span>
          <span>{formatCurrency(amountInCents, { currency: currencyToUse })}</span>
        </Element>
      )
    }

    return (
      <Element className={combinedClassName} suppressHydrationWarning>
        {formatCurrency(amountInCents, { currency: currencyToUse })}
      </Element>
    )
  }

  if (highestAmount && highestAmount !== lowestAmount) {
    // formatCurrency expects cents, so multiply by 100
    // Round to nearest integer to eliminate floating-point precision artifacts
    const lowest = Math.round(lowestAmount * 100)
    const highest = Math.round(highestAmount * 100)
    return (
      <Element className={combinedClassName} suppressHydrationWarning>
        {`${formatCurrency(lowest, { currency: currencyToUse })} - ${formatCurrency(highest, { currency: currencyToUse })}`}
      </Element>
    )
  }

  if (lowestAmount) {
    // formatCurrency expects cents, so multiply by 100
    // Round to nearest integer to eliminate floating-point precision artifacts
    const lowest = Math.round(lowestAmount * 100)
    return (
      <Element className={combinedClassName} suppressHydrationWarning>
        {`${formatCurrency(lowest, { currency: currencyToUse })}`}
      </Element>
    )
  }

  return null
}
